import React from "react";
import styles from "./styles.module.scss";
import classnames from "classnames";
import { useLayoutEffect, useRef, useState } from "react";
import classNames from "classnames";
import { Stack } from "@altimateai/lego";

interface CollapsibleFieldProps {
  label?: string;
  text?: React.ReactNode;
  defaultLines?: number;
  alignCenter?: boolean;
  textStyle?: string;
}

const CollapsibleField = ({
  label,
  text,
  defaultLines = 1,
  alignCenter = false,
  textStyle = "text-muted",
}: CollapsibleFieldProps) => {
  const parentRef = useRef<HTMLDivElement>(null);
  const childRef = useRef<HTMLSpanElement>(null);

  const [isTruncated, setIsTruncated] = useState(false);
  const [hasExcessLines, setHasExcessLines] = useState(false);
  const [isContractExpanded, setIsContractExpanded] = useState(false);

  useLayoutEffect(() => {
    const handleResize = () => {
      const childWidth = childRef.current?.offsetWidth ?? 0;
      const parentWidth = parentRef.current?.offsetWidth ?? 0;
      const clientRectsLength = childRef.current?.getClientRects().length ?? 0;
      setIsTruncated(childWidth > parentWidth || clientRectsLength > defaultLines);
      setHasExcessLines(clientRectsLength > defaultLines);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize); // Clean up the event listener
    };
  }, [defaultLines]);

  const parentStyle = {
    WebkitLineClamp: isContractExpanded ? "none" : defaultLines,
  };

  return (
    <Stack
      align={alignCenter ? "center" : undefined}
      className={alignCenter ? undefined : "mt-2 mb-2"}
    >
      {label && <div className="me-2">{label}:</div>}
      <div
        ref={parentRef}
        className={classnames(styles.parent, textStyle, {
          [styles.contract_collapse]: !isContractExpanded && defaultLines === 1,
          [styles.contract_collapse_clamp]: !isContractExpanded && defaultLines !== 1,
        })}
        style={parentStyle}
      >
        <span className={styles.child} ref={childRef}>
          {text}
        </span>
        {isContractExpanded && (
          <span
            id="collapsible-field-less"
            role="button"
            tabIndex={0}
            className="ms-2 text-primary fw-semibold cursor-pointer"
            onClick={e => {
              e.stopPropagation();
              setIsContractExpanded(false);
            }}
            onKeyDown={e => {
              if (e.key === "Enter" || e.key === " ") {
                e.stopPropagation();
                setIsContractExpanded(false);
              }
            }}
          >
            Less
          </span>
        )}
      </div>
      {isTruncated && !isContractExpanded && hasExcessLines && (
        <div
          className={classNames(
            "ms-2 text-primary fw-semibold cursor-pointer",
            styles.collapsible_field_more
          )}
          id="collapsible-field-more"
          role="button"
          tabIndex={0}
          onClick={e => {
            e.stopPropagation();
            setIsContractExpanded(true);
          }}
          onKeyDown={e => {
            if (e.key === "Enter" || e.key === " ") {
              e.stopPropagation();
              setIsContractExpanded(true);
            }
          }}
        >
          More
        </div>
      )}
    </Stack>
  );
};

export { CollapsibleField };
